import React, { useEffect } from 'react';
import {
 Card, ApplicantQualifications, ExamTable, AnimatedTabs,
} from 'components';
import { Tab } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Dispatch, IRootState } from '../../redux/store/configureStore';
import { push } from 'connected-react-router';
import { ColorStatus, Color } from '_helpers';
import { ApplicantExam, ExamApplicationStatus } from '../../api/ApiClient';
import { useLocalErrorHandler } from 'hooks';
import examHistoryPlaceholderIcon from 'components/assets/svg/spkfr/exam_history_icon.svg';
import ProfileInfoBlock from 'components/ProfileInfoBlock/ProfileInfoBlock';

const ApplicantDetailsContainer = ({
    getApplicantQualifications,
    getApplicantArchiveExams,
    getApplicantDetails,
    setErrorHandler,
    applicantQualifications,
    applicantExams,
    applicantDetail,
    applicantId,
    loadingDetails,
    apiError,
    onPush,
    tab,
}: Props) => {
    useEffect(() => {
        getApplicantDetails({ applicantId });
        if (tab === 'exams') {
            getApplicantArchiveExams({ applicantId });
        }
    }, []);

    useLocalErrorHandler(setErrorHandler);

    const EXAM_TABLE_HEADERS = ['Дата и время', 'Квалификация', 'Тип', 'Результат'];
    const params = new URLSearchParams(window.location.search);
    const from = params.get('from');

    const handleCancelClick = () => {
        if (from) {
            onPush(`/${from}`);
        } else {
            onPush('/applicants');
        }
    };

    const onSelectTab = async (key: string) => {
        if (key === 'exams') {
            await getApplicantArchiveExams({ applicantId });
        }

        if (from) {
            onPush(`/applicant-details/${applicantId}/${key}?from=${from}`);
        } else {
            onPush(`/applicant-details/${applicantId}/${key}`);
        }
    };

    const onQualificationClick = (examApplicationId: string) => {
        onPush(`/applicant-details/${applicantId}/qualifications/${examApplicationId}`);
    };

    const handleChooseExam = (examId: string) => {
        if (!applicantExams || !applicantExams.items) return;
        const selectedExam = applicantExams.items.find(x => x.examId === examId);
        if (!selectedExam) return;
        onPush(`/applicant-details/exam/${examId}/${selectedExam.examApplicationId}/exam-details/?from=${tab}`);
    };

    const prepareQualificationProps = () => ({
        qualificationList: applicantQualifications,
        handleRowClick: onQualificationClick,
        apiError,
        getQualificationsList: () => getApplicantQualifications({ applicantId }),
    });

    const getTabArr = () => [
        { tabName: 'info', title: 'Личные данные', component: renderProfileInfoBlock() },
        { tabName: 'qualifications', title: 'Квалификации', component: <ApplicantQualifications {...prepareQualificationProps()} /> },
        { tabName: 'exams', title: 'Экзамены', component: renderExamList() },
    ];

    const getExamRows = (exam: ApplicantExam) => {
        const status = exam.examApplicationStatus === ExamApplicationStatus.Passed
            ? new ColorStatus(Color.Success, 'Сдан')
            : new ColorStatus(Color.Red, 'Не сдан');
        return {
            ...exam,
            id: exam.examId,
            status,
            name: exam.qualificationTitle,
        };
    };

    const renderProfileInfoBlock = () => (
        <Card
          title="Личные данные соискателя"
          category="Информация, указанная соискателем в личном кабинете"
          show404Error={!applicantDetail}
          apiError={apiError}
          withoutBorder
        >
            {applicantDetail && <ProfileInfoBlock profile={applicantDetail} disabled hideChangeButtons />}
        </Card>
    );

    const renderExamList = () => {
        if (!applicantExams || !applicantExams.items) return null;
        return (
            <Card
              title={applicantExams.items.length === 0 ? undefined : 'Список экзаменов'}
              category={applicantExams.items.length === 0 ? undefined : 'Список экзаменов соискателя'}
              withoutBorder
              withoutPadding
            >
                <ExamTable
                  header={EXAM_TABLE_HEADERS}
                  tableRows={applicantExams.items.map(getExamRows)}
                  onChooseExam={handleChooseExam}
                  pagination={applicantExams}
                  onChangePagination={({ page, perPage }) => getApplicantArchiveExams({ page, pageSize: perPage, applicantId })}
                  placeholderIcon={examHistoryPlaceholderIcon}
                  placeholderText="Здесь будут отображаться все экзамены соискателя"
                  placeholderTitle="Нет экзаменационной истории"
                />
            </Card>
        );
    };

    const renderTabs = () => (
        <AnimatedTabs activeKey={tab} onSelect={onSelectTab} id="ApplicantDetailsContainerTabs">
            {getTabArr().map(nTab => (
                <Tab key={nTab.tabName} eventKey={nTab.tabName} title={<span className="m-0 tab-item-title">{nTab.title}</span>}>
                    {tab === nTab.tabName ? nTab.component : null}
                </Tab>
            ))}
        </AnimatedTabs>
    );

    return (
        <Card
          title={applicantDetail ? `${applicantDetail.lastName} ${applicantDetail.firstName} ${applicantDetail.middleName}` : ''}
          category={applicantDetail ? applicantDetail.id : ''}
          withoutBorder
          withoutPadding
          cardClassName="position-relative"
          withBackButton
          onClickBack={handleCancelClick}
          apiError={apiError}
          showLoader={loadingDetails}
        >

            {renderTabs()}
        </Card>
    );
};

const mapState = (state: IRootState) => ({
    applicantDetail: state.applicants.applicantDetail,
    applicantQualifications: state.applicants.applicantQualifications,
    applicantExams: state.applicants.applicantArchiveExams,
    loadingDetails: Boolean(state.loading.effects.applicants.getApplicantAsync),
    apiError: state.errors.error,
});

const mapDispatch = (dispatch: Dispatch) => ({
    onPush: (key: string) => dispatch(push(key)),
    getApplicantDetails: dispatch.applicants.getApplicantAsync,
    getApplicantQualifications: dispatch.applicants.getApplicantQualificationsAsync,
    getApplicantArchiveExams: dispatch.applicants.getApplicantArchiveExamsAsync,
    setErrorHandler: dispatch.errors.setErrorHandler,
});

export default connect(mapState, mapDispatch)(ApplicantDetailsContainer);

interface SelfProps {
    applicantId: string;
    tab: string;
}

type Props = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch> & SelfProps;
